import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  Tooltip,
  Fade,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  AccountTree as AccountTreeIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";

import { format } from "date-fns";
import { tokens } from "../../theme";



const JournalEntriesModal = ({ open, onClose, accountId, accountName, loading, journalData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return "Invalid Date";
    }
  };

  const journalColumns = [
    {
      field: "transactionDate",
      headerName: "Transaction Date",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || "Journal Entry"}
        </Typography>
      ),
    },
    {
      field: "debit",
      headerName: "Debit",
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={params.value > 0 ? colors.greenAccent[400] : colors.grey[500]}
          fontWeight="600"
        >
          {params.value > 0 ? formatCurrency(params.value) : "-"}
        </Typography>
      ),
    },
    {
      field: "credit",
      headerName: "Credit",
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={params.value > 0 ? colors.redAccent[400] : colors.grey[500]}
          fontWeight="600"
        >
          {params.value > 0 ? formatCurrency(params.value) : "-"}
        </Typography>
      ),
    },
    {
      field: "balance",
      headerName: "Running Balance",
      width: 140,
      renderCell: (params) => {
        const row = params.row;
        const runningBalance = row.debit - row.credit;
        return (
          <Chip
            label={formatCurrency(runningBalance)}
            size="small"
            sx={{
              backgroundColor: runningBalance >= 0 ? colors.greenAccent[700] : colors.redAccent[700],
              color: colors.grey[100],
              fontWeight: 600,
            }}
          />
        );
      },
    },
  ];

  const processedJournalData = journalData.map((entry, index) => ({
    id: entry.journalLineID || index,
    transactionDate: entry.createdDate,
    description: entry.description,
    debit: entry.debit,
    credit: entry.credit,
    journalEntryID: entry.journalEntryID,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: colors.primary[500],
          height: "85vh", // Increased height to utilize more screen space
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          color: colors.grey[100],
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2, // Reduced padding for a tighter header
          borderBottom: `1px solid ${colors.primary[300]}`,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="700">
            Journal Entries
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
            {accountName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.grey[100],
            "&:hover": {
              backgroundColor: colors.primary[300],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0, // Removed padding to maximize table space
          flex: 1, // Allow content to take remaining space
          overflow: "hidden", // Prevent unwanted scrollbars
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={2}>
            <CircularProgress size={40} sx={{ color: colors.greenAccent[500] }} />
            <Typography variant="body1" sx={{ color: colors.grey[300] }}>
              Loading journal entries...
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={processedJournalData}
            columns={journalColumns}
            getRowId={(row) => row.id}
            components={{ Toolbar: GridToolbar }}
            disableRowSelectionOnClick
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              height: "100%", // Use full height of DialogContent
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.primary[400],
                borderBottom: `1px solid ${colors.primary[300]}`,
                fontSize: "0.875rem",
                fontWeight: 600,
                color: colors.grey[100],
                minHeight: "40px !important", // Reduced header height
                lineHeight: "1.2 !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${colors.primary[300]}`,
                padding: "8px", // Reduced cell padding for more compact rows
                fontSize: "0.85rem",
              },
              "& .MuiDataGrid-row": {
                minHeight: "36px !important", // Reduced row height for more rows
                maxHeight: "36px !important",
                "&:hover": {
                  backgroundColor: colors.primary[400],
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${colors.primary[300]}`,
                backgroundColor: colors.primary[400],
                minHeight: "20px !important",
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "8px", // Reduced toolbar padding
                backgroundColor: colors.primary[400],
                borderBottom: `1px solid ${colors.primary[300]}`,
                "& .MuiButton-text": {
                  color: colors.grey[100],
                  fontWeight: 600,
                  fontSize: "0.85rem",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowY: "auto !important", // Ensure scrollbar appears for content
              },
            }}
            hideFooterSelectedRowCount
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: colors.primary[400],
          p: 1.5, // Reduced padding for a tighter footer
          borderTop: `1px solid ${colors.primary[300]}`,
        }}
      >
        <Chip
          label={`Total Entries: ${processedJournalData.length}`}
          size="small"
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontWeight: 600,
          }}
        />
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.primary[900],
            fontWeight: 600,
            "&:hover": {
              backgroundColor: colors.greenAccent[400],
            },
            padding: "6px 16px", // Smaller button padding
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default JournalEntriesModal;